import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { defaultSchema } from '@atlaskit/adf-schema/schema-default';

import { findFilepaths, isLinkInMatches } from '@atlaskit/editor-common/utils';
import { ADFEncoder } from '@atlaskit/renderer/utils';

import { useRovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/hooks';
import type { RovoContentBridgeSetupHandler } from '@atlassian/rovo-content-bridge-api/client';
import {
	ConfluencePageCreateCommand,
	ConfluenceUpdatePageCommand,
	ReadContentCommand,
	PrepareContentForFinalizationCommand,
	ConfluenceDeepResearchPageCreateCommand,
	type ReadContentCommandResult,
	type ConfluenceCreatePageCommandProperties,
	type ConfluenceUpdatePageCommandProperties,
	type ConfluenceDeepResearchPageCreateCommandProperties,
	ContentReadyCommand,
	ROVO_CHAT_CLIENT_ID,
	getConfluenceEditorClientId,
	ContentDestroyCommand,
} from '@atlassian/rovo-content-bridge-api-commands';
import { AiMateMarkdownTransformer } from '@atlassian/conversation-assistant-content-transformer';
import type { GenerateImageProps } from '@atlassian/ai-image-generation';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';

import { fg } from '@confluence/feature-gating';

import {
	ConfluencePageCreateCommandProcessor,
	ConfluencePageUpdateCommandProcessor,
	ConfluencePageUpdateCommandProcessorWithPartialStreaming,
} from '../../utils/aifc-streaming/command-processors';
import { log } from '../../utils/aifc-streaming/aifc-logging';

export interface UseRovoBridgeCommandListenerParams {
	editorAPI: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]> | undefined;
	onTitleChange?: (title: string) => void;
	contentId: string;
	contentContext: 'staging-area' | 'global';
	setEditorDisabled: (disabled: boolean) => void;
	forceDraftSync?: () => Promise<void>;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<void>;
	fakeGenerateAIHeaderImage?: () => Promise<void>;
}

interface OnStreamingStartParams {
	triggerFakeGenerateAIHeaderImage: boolean;
	streamType: string;
}

interface OnStreamingEndParams {
	streamType: string;
}

export const useRovoBridgeCommandListener = ({
	editorAPI,
	onTitleChange,
	contentId,
	setEditorDisabled,
	forceDraftSync,
	generateAIHeaderImage,
	fakeGenerateAIHeaderImage,
	contentContext,
}: UseRovoBridgeCommandListenerParams) => {
	const createProcessorRef = useRef<ConfluencePageCreateCommandProcessor | null>(null);
	const updateProcessorRef = useRef<
		| ConfluencePageUpdateCommandProcessor
		| ConfluencePageUpdateCommandProcessorWithPartialStreaming
		| null
	>(null);
	const isReceivingStream = useRef(false);
	const streamedAdfRef = useRef<string>('');
	const streamedMdRef = useRef<string>('');

	// Streaming Markdown -> ADF encoder for Deep Research content
	const streamingAdfEncoder = useMemo(() => {
		const pluginConfig = {
			linkify: { findFilepaths, isLinkInMatches },
			smartLinks: { regexStorage: global.localStorage },
		};
		return new ADFEncoder((schema) => new AiMateMarkdownTransformer(schema, pluginConfig, true));
	}, []);

	const onStreamingStart = useCallback(
		({ triggerFakeGenerateAIHeaderImage, streamType }: OnStreamingStartParams) => {
			if (isReceivingStream.current) {
				return;
			}

			isReceivingStream.current = true;
			setEditorDisabled(true);
			if (triggerFakeGenerateAIHeaderImage && fg('confluence_aifc_image_generation')) {
				void fakeGenerateAIHeaderImage?.();
			}

			log('Starting stream', { streamType });
		},
		[setEditorDisabled, fakeGenerateAIHeaderImage],
	);

	const onStreamingEnd = useCallback(
		({ streamType }: OnStreamingEndParams) => {
			isReceivingStream.current = false;
			setEditorDisabled(false);
			log('Ending stream', { streamType });
		},
		[setEditorDisabled],
	);

	// When the editor command streaming (e.g. appendNode) streaming is disabled, fallback to
	const fallbackPageCreateCommandCallback = useMemo(
		() => async (params: ConfluenceCreatePageCommandProperties) => {
			onStreamingStart({ triggerFakeGenerateAIHeaderImage: true, streamType: 'fallback-create' });

			if (params.data.title) {
				onTitleChange?.(params.data.title);
			}

			if (!params.data.adf) {
				return;
			}

			if (fg('convo_ai_aifc_page_create_send_chunks')) {
				// When backend is sending chunks, accrue ADF string
				streamedAdfRef.current += params.data.adf;
			} else {
				streamedAdfRef.current = params.data.adf;
			}

			if (!editorAPI) {
				return;
			}

			const streamer = new ADFStreamer(defaultSchema);
			const adfDoc = streamer.processChunk(streamedAdfRef.current).getLastDoc();
			editorAPI?.core.actions.replaceDocument(adfDoc, { scrollIntoView: false });

			if (params.isStreamComplete) {
				if (fg('confluence_aifc_image_generation')) {
					void generateAIHeaderImage?.({ adfContent: params.data.title });
				}

				if (fg('convo_ai_aifc_page_create_send_chunks')) {
					streamedAdfRef.current = '';
				}

				onStreamingEnd({ streamType: 'fallback-create' });
			}
		},
		[editorAPI, onTitleChange, onStreamingStart, onStreamingEnd, generateAIHeaderImage],
	);

	const pageCreateCommandCallback = useMemo(
		() => async (params: ConfluenceCreatePageCommandProperties) => {
			onStreamingStart({ triggerFakeGenerateAIHeaderImage: true, streamType: 'create' });

			if (params.data.title) {
				onTitleChange?.(params.data.title);
			}

			if (!params.data.adf) {
				return;
			}

			if (fg('convo_ai_aifc_page_create_send_chunks')) {
				// When backend is sending chunks, accrue ADF string
				streamedAdfRef.current += params.data.adf;
			} else {
				streamedAdfRef.current = params.data.adf;
			}

			if (!editorAPI) {
				return;
			}

			if (!createProcessorRef.current) {
				createProcessorRef.current = new ConfluencePageCreateCommandProcessor(editorAPI);
			}

			try {
				createProcessorRef.current.stream(streamedAdfRef.current);
			} catch (error) {
				// TODO: Report this properly
				// eslint-disable-next-line no-console
				console.error('Error streaming ADF in create command:', error);
			}

			if (params.isStreamComplete) {
				const adfDoc = createProcessorRef.current.finish();
				if (adfDoc) {
					editorAPI?.core.actions.replaceDocument(adfDoc, { scrollIntoView: false });
				}

				if (fg('confluence_aifc_image_generation')) {
					// TODO: Ensure this only runs once per title
					void generateAIHeaderImage?.({ adfContent: params.data.title });
				}

				if (fg('convo_ai_aifc_page_create_send_chunks')) {
					streamedAdfRef.current = '';
				}

				onStreamingEnd({ streamType: 'create' });
			}
		},
		[editorAPI, onTitleChange, onStreamingStart, onStreamingEnd, generateAIHeaderImage],
	);

	const deepResearchPageCreateCallback = useMemo(
		() => async (params: ConfluenceDeepResearchPageCreateCommandProperties) => {
			onStreamingStart({
				triggerFakeGenerateAIHeaderImage: true,
				streamType: 'deep-research-create',
			});

			if (params.data.title) {
				onTitleChange?.(params.data.title);
			}

			if (!params.data.content) {
				return;
			}

			if (params.isStreamComplete) {
				streamedMdRef.current = params.data.content;
			} else {
				streamedMdRef.current += params.data.content;
			}

			if (!editorAPI) {
				return;
			}

			const adfDoc = streamingAdfEncoder.encode(streamedMdRef.current);

			// Temporarily using replaceDocument since the editor streaming plugin does not support streaming MD or multiple ADF docs
			editorAPI?.core.actions.replaceDocument(adfDoc, { scrollIntoView: false });

			if (params.isStreamComplete) {
				streamedMdRef.current = '';

				onStreamingEnd({ streamType: 'deep-research-create' });
			}
		},
		[editorAPI, onTitleChange, onStreamingStart, onStreamingEnd, streamingAdfEncoder],
	);

	const pageUpdateCommandCallback = useMemo(
		() => async (params: ConfluenceUpdatePageCommandProperties) => {
			onStreamingStart({ triggerFakeGenerateAIHeaderImage: false, streamType: 'update' });

			if (!params.data.toolCalls || !editorAPI) {
				return;
			}

			if (!updateProcessorRef.current) {
				if (fg('confluence_aifc_partial_update_streaming')) {
					updateProcessorRef.current = new ConfluencePageUpdateCommandProcessorWithPartialStreaming(
						editorAPI,
					);
				} else {
					updateProcessorRef.current = new ConfluencePageUpdateCommandProcessor(editorAPI);
				}
			}

			try {
				if (fg('convo_ai_aifc_update_send_chunks')) {
					updateProcessorRef.current.streamChunk(params.data.toolCalls, params.isStreamComplete);
				} else {
					updateProcessorRef.current.streamAccumulated(
						params.data.toolCalls,
						params.isStreamComplete,
					);
				}
			} catch (error) {
				// TODO: Report this properly
				// eslint-disable-next-line no-console
				console.error('Error streaming tool calls in update command:', error);
			}

			if (params.isStreamComplete) {
				updateProcessorRef.current.finish();
				onStreamingEnd({ streamType: 'update' });
			}
		},
		[editorAPI, onStreamingStart, onStreamingEnd],
	);

	const readContentCommandCallback = useMemo(
		() => async () => {
			return new Promise<ReadContentCommandResult>((resolve) => {
				const defaultResult = {
					contentType: 'page' as const,
					pageBody: undefined,
					contentId,
					selectionFragment: '',
					selectionLocalIds: '',
				};
				try {
					const requestDocument = editorAPI?.core?.actions?.requestDocument;
					if (requestDocument) {
						requestDocument((document) => {
							const selection = editorAPI?.aiExperience?.actions.getAISelectionContext();
							log('Read content result', { contentId, document, selection });

							resolve({
								contentType: 'page',
								pageBody: document,
								contentId,
								selectionFragment: selection?.selectionFragment
									? JSON.stringify(selection.selectionFragment)
									: '',
								selectionLocalIds: selection?.localIds ? JSON.stringify(selection.localIds) : '',
							});
						});
					} else {
						// TODO AFC-194: report this error
						resolve(defaultResult);
					}
				} catch {
					// TODO AFC-194: report this error
					resolve(defaultResult);
				}
			});
		},
		[editorAPI, contentId],
	);

	const prepareContentForFinalizationCommandCallback = useMemo(
		() => async () => {
			return new Promise<{ success: boolean }>(async (resolve) => {
				log('Preparing content for finalization');
				await forceDraftSync?.();
				resolve({ success: true });
			});
		},
		[forceDraftSync],
	);

	const mountCallback: RovoContentBridgeSetupHandler = useMemo(
		() => (on) => {
			on(
				ConfluencePageCreateCommand,
				!fg('platform_editor_ai_skip_free_gen_streaming')
					? pageCreateCommandCallback
					: fallbackPageCreateCommandCallback,
			);
			on(ConfluenceUpdatePageCommand, pageUpdateCommandCallback);
			on(ReadContentCommand, readContentCommandCallback);
			on(PrepareContentForFinalizationCommand, prepareContentForFinalizationCommandCallback);
			on(ConfluenceDeepResearchPageCreateCommand, deepResearchPageCreateCallback);
		},
		[
			pageCreateCommandCallback,
			deepResearchPageCreateCallback,
			fallbackPageCreateCommandCallback,
			pageUpdateCommandCallback,
			readContentCommandCallback,
			prepareContentForFinalizationCommandCallback,
		],
	);

	const { ready, send, teardown } = useRovoContentBridgeAPI(
		getConfluenceEditorClientId(contentId),
		mountCallback,
	);

	const scheduleTeardown = useCallback(() => {
		const run = () => {
			/**
			 * @description We do NOT want to yield a return value here. It can cause certain unload
			 * handlers to display a window alert with "Are you sure you want to leave this page?".
			 * Specifically, see @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event,
			 * which is remarked to be a cancellable event by browsers.
			 */
			teardown();
			// only needs to run once - the JS context is gone after this, so, unneeded afterward
			unmount();
		};

		const unmount = () => {
			// Although unnecessary (the JS context is being destroyed), we remove the event listeners
			// to avoid any potential memory leaks due to hanging references - side effects can cause this.
			window.removeEventListener('unload', run);
			window.removeEventListener('pagehide', run);
		};

		// Below, we attempt to teardown on different event handlers which we know will trigger
		// on complete unmount of the document in this JS context. Note: Same-origin iframes are
		// indeed considered their own JS context here; just that they have access to other contexts.
		// Browsers report `unload` and `beforeunload` to be unreliable. Hence, we leverage a chain
		// of these events. See official MDN documentation for more (linked below).

		/**
		 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
		 * TODO - bring beforeunload back by consolidating handlers per discussion at -
		 * https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/207747/overview#comment-674441389
		 */
		// window.addEventListener('beforeunload', run, { once: true });
		/**
		 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event
		 */
		window.addEventListener('unload', run, { once: true });
		/**
		 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event
		 */
		window.addEventListener('pagehide', run, { once: true });
	}, [teardown]);

	useEffect(() => {
		if (!editorAPI) {
			return;
		}

		ready();
		void send(ContentReadyCommand, {
			receiverId: ROVO_CHAT_CLIENT_ID,
			contentId,
			contentType: 'page',
			canProvideContext: true,
			contentContext,
		});

		// teardown case 1: when React unmounts an iframe, it does not have context around it. Hence,
		// we need to ensure that if the iframe is unmounted BEFORE the below useEffect hook runs, then
		// we are correctly tearing down the client from the bridge API.
		scheduleTeardown();

		return () => {
			try {
				// teardown case 2: in non-iframe use cases (e.g. SPA transitions on real content),
				// we need to ensure client management is 1-1 with the content we are on.
				void send(ContentDestroyCommand, {
					receiverId: ROVO_CHAT_CLIENT_ID,
					contentId,
					contentType: 'page',
					contentContext,
				});
				teardown();
			} catch {
				// TODO - handle errors; should go to Sentry
				// could be sent in future by navigation lockout
			}
		};
	}, [ready, editorAPI, contentId, scheduleTeardown, teardown, send, contentContext]);
};
